/**
 * Plan-tier access, in one place.
 *
 * There were two hand-maintained tier tables — one in GrowHub, one in
 * DashboardSidebar — and they had already drifted apart:
 *
 *   - the sidebar's omitted `scale` (a real paid tier above business), so
 *     `PLAN_ORDER['scale']` was undefined and a Scale customer ranked as free
 *   - the sidebar used `|| 0`, which floors any unknown tier to free instead
 *     of failing visibly
 *   - the sidebar had no trial uplift, so trialing hosts were ranked `free`
 *     even though the product treats them as Serve
 *   - the sidebar checked `role === 'admin'` only, missing `super_admin`,
 *     which CLAUDE.md says bypasses all plan limits
 *
 * Every one of those is a "paying customer silently loses a feature" bug, so
 * the table lives here and both surfaces call the same function.
 *
 * Keep in sync with the backend plan keys (stripeService PLANS /
 * middleware/planLimits).
 */

/** Tier ordering. Must include every plan key the backend can persist. */
export const PLAN_RANK = {
  free: 0,
  starter: 1,
  professional: 2,
  business: 3,
  scale: 4,
  enterprise: 5,
};

/** Admin roles bypass plan gating entirely (see CLAUDE.md "Admin has no usage limits"). */
export function isAdminRole(role) {
  return role === 'admin' || role === 'super_admin';
}

/**
 * The plan we should treat this user as being on.
 * Trial hosts get Serve-tier (`professional`) access — the product sells the
 * trial as "every Serve feature free", so gating them at their raw stored
 * plan (`free`) would contradict that.
 */
export function getEffectivePlan(currentUser, subscriptionStatus) {
  if (subscriptionStatus?.isTrialing) return 'professional';
  return currentUser?.plan || 'free';
}

/**
 * Numeric rank for gating comparisons. Admins are Infinity.
 * Uses `??` rather than `||` so an unknown tier is distinguishable from free
 * only by intent — an unknown key still yields 0, but we don't silently swallow
 * a legitimate rank of 0.
 */
export function getUserPlanRank(currentUser, subscriptionStatus) {
  if (isAdminRole(currentUser?.role)) return Infinity;
  return PLAN_RANK[getEffectivePlan(currentUser, subscriptionStatus)] ?? 0;
}

/** Does this user meet or exceed `requiredPlan`? */
export function hasPlanAccess(currentUser, subscriptionStatus, requiredPlan) {
  return getUserPlanRank(currentUser, subscriptionStatus) >= (PLAN_RANK[requiredPlan] ?? 0);
}

/**
 * May this user use the product, and if not, were they a customer?
 *
 * With UNIFIED_PLAN_WALL on the server, GET /me carries `planStanding` — the
 * server's verdict from the same rule the API wall and the evergreen lapse
 * sweep use — and `planWallUnified: true`; the answer is that verdict. Without
 * it (switch off, or a user object cached from before the field existed) it
 * is the old client rule, which lets in any paid-looking `plan` whatever the
 * subscription status. A seat on a paying team and an admin role always pass.
 *
 * `lapsed`: walled out, but was a customer (a paid plan field, a trial used,
 * a grace period running, or a canceled / past-due / expired status). They get
 * read-only access and "your plan has ended" copy instead of the cold wall.
 */
export function planAccessFor(user) {
  if (!user) return { hasPlanAccess: false, lapsed: false };
  const hasPaidPlan = !!user.plan && user.plan !== 'none' && user.plan !== 'free';
  const isActiveSubscription = user.subscriptionStatus === 'active' && hasPaidPlan;
  const unified = user.planWallUnified === true && typeof user.planStanding?.active === 'boolean';
  const hasPlanAccess =
    isAdminRole(user.role) ||
    !!user.teamPlan ||
    (unified
      ? user.planStanding.active
      : hasPaidPlan || isActiveSubscription || !!user.isTrialing);
  const lapsed =
    !hasPlanAccess &&
    (hasPaidPlan ||
      !!user.hasUsedTrial ||
      !!user.planLapse ||
      ['canceled', 'past_due', 'expired'].includes(user.subscriptionStatus));
  return { hasPlanAccess, lapsed };
}
